import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getMemberDetail,
  getMemberSurvey,
  downloadFile,
  downloadFilesZip,
  getEmailLogs,
  updateMemberStatus,
  sendInviteEmail,
  sendPasswordResetEmail,
} from '../../services/adminService';
import ConfirmModal from '../../components/ConfirmModal';
import './AdminMemberDetailPage.css';

const AdminMemberDetailPage = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [selectedTaxYear, setSelectedTaxYear] = useState(null);
  const [availableTaxYears, setAvailableTaxYears] = useState([]);
  const [actionMessage, setActionMessage] = useState('');
  const [actionMessageType, setActionMessageType] = useState('');
  const actionMessageTimeoutRef = useRef(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState('');

  useEffect(() => {
    loadData();
  }, [memberId, selectedTaxYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const memberData = await getMemberDetail(memberId);
      setMember(memberData);

      // 과세연도 목록 추출
      const taxYears = [];
      if (memberData.tax_year) taxYears.push(memberData.tax_year);
      if (memberData.dependents) {
        memberData.dependents.forEach(dep => {
          if (dep.tax_year && !taxYears.includes(dep.tax_year)) {
            taxYears.push(dep.tax_year);
          }
        });
      }
      setAvailableTaxYears(taxYears.sort((a, b) => b - a));
      
      if (taxYears.length > 0 && !selectedTaxYear) {
        setSelectedTaxYear(taxYears[0]);
      }

      if (selectedTaxYear) {
        const [surveyData, emailLogsData] = await Promise.all([
          getMemberSurvey(memberId, selectedTaxYear).catch(() => null),
          getEmailLogs(memberId).catch(() => ({ logs: [] })),
        ]);
        setSurvey(surveyData);
        setEmailLogs(emailLogsData.logs || emailLogsData || []);
      }
    } catch (error) {
      console.error('데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const showActionMessage = (message, type) => {
    setActionMessage(message);
    setActionMessageType(type);
    if (actionMessageTimeoutRef.current) {
      clearTimeout(actionMessageTimeoutRef.current);
    }
    actionMessageTimeoutRef.current = setTimeout(() => {
      setActionMessage('');
      setActionMessageType('');
    }, 3000);
  };

  const openConfirm = (action, message) => {
    setConfirmAction(action);
    setConfirmMessage(message);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmMessage('');
    setConfirmAction('');
  };

  const handleTaxYearChange = (year) => {
    setSelectedTaxYear(year);
    setSurvey(null);
  };

  const performStatusChange = async () => {
    if (member.status !== 'submitted') {
      showActionMessage('제출 완료 상태인 회원만 작성 중으로 변경할 수 있습니다.', 'error');
      return;
    }

    try {
      await updateMemberStatus(memberId, 'in_progress');
      setMember((prev) => (prev ? { ...prev, status: 'in_progress' } : prev));
      showActionMessage('회원 상태가 변경되었습니다.', 'success');
    } catch (error) {
      showActionMessage('상태 변경에 실패했습니다.', 'error');
      console.error('상태 변경 실패:', error);
    }
  };

  const performSendInvite = async () => {
    try {
      await sendInviteEmail(memberId);
      showActionMessage('초대 메일이 발송되었습니다.', 'success');
    } catch (error) {
      showActionMessage('초대 메일 발송에 실패했습니다.', 'error');
      console.error('초대 메일 발송 실패:', error);
    }
  };

  const performSendPasswordReset = async () => {
    try {
      await sendPasswordResetEmail(memberId);
      showActionMessage('비밀번호 재설정 메일이 발송되었습니다.', 'success');
    } catch (error) {
      showActionMessage('비밀번호 재설정 메일 발송에 실패했습니다.', 'error');
      console.error('비밀번호 재설정 메일 발송 실패:', error);
    }
  };

  const handleStatusChange = () => {
    if (member.status !== 'submitted') {
      showActionMessage('제출 완료 상태인 회원만 작성 중으로 변경할 수 있습니다.', 'error');
      return;
    }
    openConfirm('statusChange', '회원 상태를 "작성 중"으로 변경하시겠습니까?');
  };

  const handleSendInvite = () => {
    openConfirm('sendInvite', '초대 메일을 발송하시겠습니까?');
  };

  const handleSendPasswordReset = () => {
    openConfirm('sendPasswordReset', '비밀번호 재설정 메일을 발송하시겠습니까?');
  };

  const handleConfirm = async () => {
    const action = confirmAction;
    closeConfirm();
    if (action === 'statusChange') {
      await performStatusChange();
    } else if (action === 'sendInvite') {
      await performSendInvite();
    } else if (action === 'sendPasswordReset') {
      await performSendPasswordReset();
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const blob = await downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert('파일 다운로드에 실패했습니다.');
      console.error('파일 다운로드 실패:', error);
    }
  };

  const handleDownloadZip = async () => {
    try {
      const blob = await downloadFilesZip([memberId]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${member?.email || 'files'}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert('파일 압축 다운로드에 실패했습니다.');
      console.error('파일 압축 다운로드 실패:', error);
    }
  };

  if (loading) {
    return <div className="loading">로딩 중...</div>;
  }

  if (!member) {
    return <div className="error">회원 정보를 찾을 수 없습니다.</div>;
  }

  return (
    <div className="admin-member-detail-page">
      <div className="page-header">
        <button onClick={() => navigate(-1)} className="back-btn">
          ← 뒤로가기
        </button>
        <h1>회원 상세 정보</h1>
      </div>

      <div className="member-info-card">
        <div className="member-header">
          <h2>{member.name}</h2>
          <div className="member-actions">
            <button onClick={handleSendInvite} className="action-btn invite-btn">
              초대 메일 발송
            </button>
            <button onClick={handleSendPasswordReset} className="action-btn password-reset-btn">
              비밀번호 재설정 및 발송
            </button>
            {member.status === 'submitted' && (
              <button onClick={handleStatusChange} className="action-btn status-change-btn">
                작성 중으로 변경
              </button>
            )}
          </div>
        </div>
        {actionMessage && (
          <div className={`action-message ${actionMessageType}`}>
            {actionMessage}
          </div>
        )}
        <div className="info-grid">
          <div className="info-item">
            <label>회사명</label>
            <span>{member.company}</span>
          </div>
          <div className="info-item">
            <label>이메일</label>
            <span>{member.email}</span>
          </div>
          <div className="info-item">
            <label>과세연도</label>
            <span>{member.tax_year || '-'}</span>
          </div>
          <div className="info-item">
            <label>주민등록번호</label>
            <span>{member.resident_number || '-'}</span>
          </div>
          <div className="info-item">
            <label>주소지</label>
            <span>{member.address || '-'}</span>
          </div>
          <div className="info-item">
            <label>제출 현황</label>
            <span className={`status-badge status-${member.status}`}>
              {member.status === 'not_accessed'
                ? '미접속'
                : member.status === 'in_progress'
                ? '작성 중'
                : '제출 완료'}
            </span>
          </div>
        </div>
      </div>

      {availableTaxYears.length > 0 && (
        <div className="tax-year-selector">
          <label>조회할 과세연도:</label>
          <select
            value={selectedTaxYear || ''}
            onChange={(e) => handleTaxYearChange(parseInt(e.target.value))}
          >
            {availableTaxYears.map(year => (
              <option key={year} value={year}>{year}년</option>
            ))}
          </select>
        </div>
      )}

      <div className="tabs">
        <button
          className={activeTab === 'info' ? 'active' : ''}
          onClick={() => setActiveTab('info')}
        >
          기본정보
        </button>
        <button
          className={activeTab === 'survey' ? 'active' : ''}
          onClick={() => setActiveTab('survey')}
        >
          설문지
        </button>
        <button
          className={activeTab === 'files' ? 'active' : ''}
          onClick={() => setActiveTab('files')}
        >
          첨부파일
        </button>
        <button
          className={activeTab === 'email-logs' ? 'active' : ''}
          onClick={() => setActiveTab('email-logs')}
        >
          이메일 발송 이력
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'info' && (
          <div className="info-content">
            <h3>부양가족 정보</h3>
            {member.dependents && member.dependents.length > 0 ? (
              <div className="table-wrapper">
                <table className="dependents-table">
                  <thead>
                    <tr>
                      <th>성함</th>
                      <th>가족관계</th>
                      <th>관계</th>
                      <th>주민등록번호</th>
                      <th>소득금액 100만원 이하</th>
                      <th>장애인</th>
                    </tr>
                  </thead>
                  <tbody>
                    {member.dependents
                      .filter(dep => !selectedTaxYear || dep.taxYear === selectedTaxYear || dep.tax_year === selectedTaxYear)
                      .map((dep, index) => {
                        // 필드명 통일 (camelCase 우선, 없으면 snake_case)
                        const familyRelationValue = dep.familyRelation || dep.family_relation || '';
                        
                        // family_relation 값을 한글로 변환
                        const familyRelationMap = {
                          'self': '본인',
                          'spouse': '배우자',
                          'father': '아버지',
                          'mother': '어머니',
                          'grandfather': '할아버지',
                          'grandmother': '할머니',
                          'spouse_father': '배우자의 아버지',
                          'spouse_mother': '배우자의 어머니',
                          'spouse_grandfather': '배우자의 할아버지',
                          'spouse_grandmother': '배우자의 할머니',
                          'child': '자녀',
                          'sibling': '형제자매',
                        };
                        const familyRelation = familyRelationValue ? (familyRelationMap[familyRelationValue] || familyRelationValue) : '-';
                        
                        const relationship = dep.relationship || '-';
                        const residentNumber = dep.residentNumber || dep.resident_number || '-';
                        const incomeBelow1m = dep.incomeBelow1m !== undefined ? dep.incomeBelow1m : (dep.income_below_1m !== undefined ? dep.income_below_1m : false);
                        const isDisabled = dep.isDisabled !== undefined ? dep.isDisabled : (dep.is_disabled !== undefined ? dep.is_disabled : false);
                        
                        return (
                          <tr key={index}>
                            <td>{dep.name}</td>
                            <td>{familyRelation}</td>
                            <td>{relationship}</td>
                            <td>{residentNumber}</td>
                            <td>{incomeBelow1m ? '예' : '아니오'}</td>
                            <td>{isDisabled ? '예' : '아니오'}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>부양가족 정보가 없습니다.</p>
            )}
          </div>
        )}

        {activeTab === 'survey' && (
          <div className="survey-content">
            {survey && survey.length > 0 ? (
              <div className="table-wrapper">
                <table className="survey-table">
                  <thead>
                    <tr>
                      <th>항목명</th>
                      <th>값</th>
                    </tr>
                  </thead>
                  <tbody>
                    {survey.map((item, index) => (
                      <tr key={index}>
                        <td>{item.excel_column_name || item.question || '-'}</td>
                        <td>{item.value || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>설문지 정보가 없습니다.</p>
            )}
          </div>
        )}

        {activeTab === 'files' && (
          <div className="files-content">
            <div className="files-header">
              <h3>공제자료 파일</h3>
              {member.files && member.files.length > 0 && (
                <button onClick={handleDownloadZip} className="download-zip-btn">
                  전체 파일 압축 다운로드
                </button>
              )}
            </div>
            {member.files && member.files.length > 0 ? (
              <div className="table-wrapper">
                <table className="files-table">
                  <thead>
                    <tr>
                      <th>파일명</th>
                      <th>파일 크기</th>
                      <th>업로드 일시</th>
                      <th>작업</th>
                    </tr>
                  </thead>
                  <tbody>
                    {member.files
                      .filter(file => !selectedTaxYear || file.tax_year === selectedTaxYear)
                      .map((file) => (
                        <tr key={file.id}>
                          <td className="file-name-cell">{file.original_name || file.name}</td>
                          <td>{file.file_size ? (file.file_size / 1024 / 1024).toFixed(2) : '-'} MB</td>
                          <td>{file.uploaded_at ? new Date(file.uploaded_at).toLocaleString('ko-KR') : '-'}</td>
                          <td>
                            <button
                              onClick={() => handleDownloadFile(file.id, file.original_name || file.name)}
                              className="download-btn"
                            >
                              다운로드
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>첨부된 파일이 없습니다.</p>
            )}
          </div>
        )}

        {activeTab === 'email-logs' && (
          <div className="email-logs-content">
            <h3>이메일 발송 이력</h3>
            {emailLogs.length > 0 ? (
              <div className="table-wrapper">
                <table className="email-logs-table">
                  <thead>
                    <tr>
                      <th>발송일시</th>
                      <th>이메일 타입</th>
                      <th>수신자 이메일</th>
                      <th>성공 여부</th>
                      <th>오류 메시지</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{new Date(log.sent_at).toLocaleString('ko-KR')}</td>
                        <td>{log.email_type === 'invite' ? '초대' : '비밀번호 재설정'}</td>
                        <td>{log.recipient_email}</td>
                        <td>
                          <span className={log.success ? 'success-badge' : 'error-badge'}>
                            {log.success ? '성공' : '실패'}
                          </span>
                        </td>
                        <td>{log.error_message || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>발송 이력이 없습니다.</p>
            )}
          </div>
        )}
      </div>
      <ConfirmModal
        open={confirmOpen}
        title="확인"
        message={confirmMessage}
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default AdminMemberDetailPage;
